import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useResource } from '../lib/useResource';
import { Card, Empty, Loading, Notice, Pill, Stat, exactly } from '../components/ui';

interface FleetSummary {
  totalDevices: number;
  devicesByStatus: Record<string, number>;
  online: number;
  notReporting: number;
  policyAcknowledged: number;
  compliance: {
    devicesAssessed: number;
    compliant: number;
    nonCompliant: number;
    averageScore: number;
    encrypted: number;
    encryptionCoveragePercent: number;
  };
  alerts: {
    openTotal: number;
    openBySeverity: Record<string, number>;
    raisedLastSevenDays: number;
  };
  commands: { awaitingApproval: number; inFlight: number };
  generatedAt: string;
}

interface NonCompliantDevice {
  deviceId: string;
  assetTag: string | null;
  hostname: string | null;
  platform: string | null;
  complianceScore: number;
  diskEncryptionState: string;
  failingControls: string;
  lastEvaluatedAt: string;
}

export function Dashboard() {
  const fleet = useResource(() => api.get<FleetSummary>('/api/v1/dashboard/fleet'), []);
  const remediation = useResource(
    () => api.get<NonCompliantDevice[]>('/api/v1/dashboard/non-compliant'),
    [],
  );

  if (fleet.loading && !fleet.data) return <Loading />;
  if (fleet.error) return <Notice kind="error">{fleet.error}</Notice>;
  if (!fleet.data) return null;

  const summary = fleet.data;
  const critical = summary.alerts.openBySeverity.CRITICAL ?? 0;
  const high = summary.alerts.openBySeverity.HIGH ?? 0;

  return (
    <>
      <div className="page-head">
        <h1>Fleet overview</h1>
        <p>
          The figures behind the monthly security and compliance report to Executive Management.
          Generated {exactly(summary.generatedAt)}.
        </p>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="Managed devices"
          value={summary.totalDevices}
          note={`${summary.online} online now`}
        />
        <Stat
          label="Meeting the baseline"
          value={`${summary.compliance.compliant}/${summary.compliance.devicesAssessed}`}
          note={`average score ${summary.compliance.averageScore}`}
          tone={summary.compliance.nonCompliant > 0 ? 'alarm' : 'good'}
        />
        <Stat
          label="Disk encryption"
          value={`${summary.compliance.encryptionCoveragePercent}%`}
          note={`${summary.compliance.encrypted} of ${summary.compliance.devicesAssessed} encrypted`}
          tone={summary.compliance.encryptionCoveragePercent === 100 ? 'good' : 'alarm'}
        />
        <Stat
          label="Not reporting"
          value={summary.notReporting}
          note="no check-in for over 48 hours"
          tone={summary.notReporting > 0 ? 'alarm' : 'good'}
        />
      </div>

      <div className="grid cols-2">
        <Card title="Open alerts" extra={<Link to="/alerts">View all</Link>}>
          <div className="grid cols-3" style={{ marginBottom: 12 }}>
            <Stat label="Open" value={summary.alerts.openTotal} />
            <Stat label="Critical" value={critical} tone={critical > 0 ? 'alarm' : undefined} />
            <Stat label="High" value={high} tone={high > 0 ? 'alarm' : undefined} />
          </div>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
            {summary.alerts.raisedLastSevenDays} raised in the last seven days.
          </p>
        </Card>

        <Card title="Commands" extra={<Link to="/commands">View all</Link>}>
          <div className="grid cols-2" style={{ marginBottom: 12 }}>
            <Stat
              label="Awaiting approval"
              value={summary.commands.awaitingApproval}
              note="destructive actions need two administrators"
              tone={summary.commands.awaitingApproval > 0 ? 'alarm' : undefined}
            />
            <Stat label="In flight" value={summary.commands.inFlight} note="sent, not yet confirmed" />
          </div>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
            {summary.policyAcknowledged} of {summary.totalDevices} devices have a recorded policy
            acknowledgement.
          </p>
        </Card>
      </div>

      <Card
        title="Remediation queue"
        extra={remediation.data ? `${remediation.data.length} device(s) failing a control` : undefined}
      >
        {remediation.loading && !remediation.data && <Loading />}
        {remediation.error && <Notice kind="error">{remediation.error}</Notice>}
        {remediation.data?.length === 0 && <Empty>Every assessed device meets the security baseline.</Empty>}
        {remediation.data && remediation.data.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Host</th>
                  <th>Platform</th>
                  <th className="num">Score</th>
                  <th>Encryption</th>
                  <th>Failing controls</th>
                </tr>
              </thead>
              <tbody>
                {remediation.data.map((device) => (
                  <tr key={device.deviceId}>
                    <td>
                      <Link to={`/devices/${device.deviceId}`}>{device.assetTag ?? 'unknown'}</Link>
                    </td>
                    <td>{device.hostname ?? '—'}</td>
                    <td>{device.platform ?? '—'}</td>
                    <td className="num">
                      {device.complianceScore}
                      <div className={`bar ${device.complianceScore < 50 ? 'stop' : 'warn'}`}>
                        <span style={{ width: `${device.complianceScore}%` }} />
                      </div>
                    </td>
                    <td>
                      <Pill tone={device.diskEncryptionState === 'ENCRYPTED' ? 'ok' : 'stop'}>
                        {device.diskEncryptionState.toLowerCase()}
                      </Pill>
                    </td>
                    <td>{formatControls(device.failingControls)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/** The API stores failing controls as a JSON array string; render them as readable labels. */
function formatControls(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as string[];
    return parsed.map((control) => control.replace(/_/g, ' ').toLowerCase()).join(', ') || '—';
  } catch {
    return raw;
  }
}
